import React, {
  MutableRefObject,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import { StateContext } from '../../../GlobalStateStore';
import DataSource from '../../../api';
import {
  SampledAudio,
  CanvasState,
  processBuffer,
  CLClickstreamWithMediaLegInfo,
} from '../utils/ContactListeningHelpers';
import WaveCanvas from './WaveCanvas';
import Checkbox from '@amzn/meridian/checkbox';
import Button from '@amzn/meridian/button';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Select, { SelectOption } from '@amzn/meridian/select';
import Icon from '@amzn/meridian/icon';
import play from '@amzn/meridian-tokens/base/icon/play';
import pause from '@amzn/meridian-tokens/base/icon/pause';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import { useIntl } from 'react-intl';
import useCustomTracking from '../../../Util/useCustomTracking';
import { logger } from '../../../Util';
import { QueryVariablesContactListeningData } from '../../../api/Naws';
import {
  ContactData,
  ContactListeningQueryTypeOption,
  defaultContactData,
} from '../../../api/ContactListening/schema';
import DataState from '../../../Types/DataState';

export interface AudioPlayerProps {
  firstBase64?: string;
  nextBlobParams?: QueryVariablesContactListeningData;
  id?: string;
  CLClickstreamWithMediaLegInfo: CLClickstreamWithMediaLegInfo;
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  firstBase64,
  nextBlobParams,
  id,
  CLClickstreamWithMediaLegInfo,
}) => {
  const intl = useIntl();
  const [duration, setDuration] = useState<number>();
  const [sampledAudio, setSampledAudio] = useState<SampledAudio>();
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [playAudio, setPlayAudio] = useState(false);
  const [skipSilence, setSkipSilence] = useState<boolean>(true);
  const [canvasState, setCanvasState] = useState<CanvasState>(
    firstBase64 ? CanvasState.PREPARING : CanvasState.NO_DATA
  );
  const [canvasDrawn, setCanvasDrawn] = useState<boolean>(false);
  const [playSpeed, setPlaySpeed] = useState('1');
  const [blobUrl, setBlobUrl] = React.useState<string>();
  const [buffers, setBuffers] = React.useState<Buffer[]>([]);
  const [yetToLoadParts, setYetToLoadParts] = useState<boolean>(true);
  const [updatedBlobParams, setUpdatedBlobParams] = useState<
    QueryVariablesContactListeningData | undefined
  >(nextBlobParams);
  const [audioBuffer, setAudioBuffer] = React.useState<AudioBuffer>();
  const [nextContactPart, setNextContactPart] =
    React.useState<ContactData>(defaultContactData);
  const [nextContactPartLoaded, setNextContactPartLoaded] =
    React.useState<string>(DataState.INITIALIZED);

  const audioRef = useRef() as MutableRefObject<HTMLAudioElement>;
  // Tracks whether this is the first blob load (auto-play) vs subsequent swaps (preserve play/pause state)
  const isInitialLoad = useRef(true);
  // Suppresses onTimeUpdate during blob src swaps to prevent the UI from briefly flashing 0:00
  const isSwapping = useRef(false);

  const hasMedia =
    canvasState === CanvasState.PREPARING || canvasState === CanvasState.READY;

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { requesterUsername, isTm } = inspireOptions.data;

  const onTimeUpdate = (event: SyntheticEvent<HTMLAudioElement>) => {
    // Skip updates during blob swaps — setting audio.src resets currentTime to 0 briefly
    if (isSwapping.current) return;
    setCurrentTime(event.currentTarget.currentTime);

    // skip if in silence gap
    if (sampledAudio && skipSilence) {
      const second = Math.floor(currentTime);
      if (
        sampledAudio.gapsArray.length > 0 &&
        sampledAudio.gapsArray[second] !== -1
      ) {
        audioRef.current.currentTime = sampledAudio.gapsArray[second];
      }
    }
  };

  const setRatio = (ratio: number) => {
    if (sampledAudio) {
      const time = ratio * sampledAudio.duration;
      audioRef.current.currentTime = time;
    }
  };

  // When the current blob finishes playing, check if more chunks have been downloaded
  // in the background. If so, create a combined blob and seamlessly continue playback.
  // This avoids swapping on every chunk arrival (which causes audio spikes) — instead
  // we only swap when the audio element actually runs out of data.
  const onAudioEnded = () => {
    if (yetToLoadParts && buffers.length > 1 && nextBlobParams) {
      isSwapping.current = true;
      const savedTime = audioRef.current.currentTime;
      const blob = new Blob(buffers, {
        type:
          nextBlobParams.queryType?.queryTypeOption ==
            ContactListeningQueryTypeOption.CONNECT_PART ||
          nextBlobParams.queryType?.queryTypeOption ==
            ContactListeningQueryTypeOption.DART_CONNECT_PART
            ? 'audio/wav'
            : 'audio/mpeg',
      });
      audioRef.current.src = URL.createObjectURL(blob);
      audioRef.current.oncanplay = () => {
        audioRef.current.currentTime = savedTime;
        // Setting audio.src resets playbackRate to 1 — restore user's selected speed
        audioRef.current.playbackRate = Number(playSpeed);
        audioRef.current.play();
        setPlayAudio(true);
        isSwapping.current = false;
        logger.info(
          `[AudioPlayer] Blob extended with ${buffers.length} chunks, resuming at ${savedTime.toFixed(1)}s`
        );
        audioRef.current.oncanplay = null;
      };
    } else {
      setPlayAudio(false);
    }
  };

  const buttonClick = (event: CustomEvent) => {
    playButtonClick();
    if (playAudio) {
      audioRef.current.pause();
      setPlayAudio(false);
    } else {
      audioRef.current.play();
      setPlayAudio(true);
    }
  };

  const formatAsDuration = (seconds: number): string => {
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds) % 60;
    return (min < 10 ? ' ' : '') + min + ':' + (sec < 10 ? '0' : '') + sec;
  };

  const locationAsStringCurrent = (): string => {
    return formatAsDuration(currentTime);
  };

  const locationAsStringDuration = (): string => {
    return ' /' + formatAsDuration(duration || 0);
  };

  const CLClickStreamCommonTags = {
    'data-csa-c-type': 'button',
    'data-csa-c-tag1': CLClickstreamWithMediaLegInfo.tag1,
    'data-csa-c-tag2': CLClickstreamWithMediaLegInfo.tag2,
    'data-csa-c-tag3': CLClickstreamWithMediaLegInfo.tag3,
    'data-csa-c-tag4': CLClickstreamWithMediaLegInfo.tag4,
    'data-csa-c-tag5': CLClickstreamWithMediaLegInfo.tag5,
    'data-csa-c-contactid': CLClickstreamWithMediaLegInfo.contactID,
    'data-csa-c-connect-contact-id':
      CLClickstreamWithMediaLegInfo.connectContactId,
    'data-csa-c-is-tm': isTm,
    'data-csa-c-username': requesterUsername,
    'data-csa-c-call-duration-seconds': duration,
  };

  const [PlayButtonTrackingTag, playButtonClick] = useCustomTracking(
    'contact-listening-play-button',
    'contact-listening-play-button',
    {
      ...CLClickStreamCommonTags,
      'data-csa-c-element-slot-id': 'contact-listening-play-button',
    }
  );
  const [SkipSilenceTrackingTag, skipSilenceClick] = useCustomTracking(
    'contact-listening-skip-silence',
    'contact-listening-skip-silence',
    {
      ...CLClickStreamCommonTags,
      'data-csa-c-element-slot-id': 'contact-listening-skip-silence',
      'data-csa-c-element-skip-silence': skipSilence,
    }
  );

  const [ChangeSpeedTrackingTag, changeSpeedClick] = useCustomTracking(
    'contact-listening-change-speed',
    'contact-listening-change-speed',
    {
      ...CLClickStreamCommonTags,
      'data-csa-c-element-slot-id': 'contact-listening-change-speed',
      'data-csa-c-element-speed-rate': playSpeed,
    }
  );

  const [
    ContactListeningStartedClickstreamTag,
    emitContactListeningStartedClickstreamEvent,
  ] = useCustomTracking(
    'contact-listening-streaming-started',
    'contact-listening-streaming-started',
    {
      ...CLClickStreamCommonTags,
      'data-csa-c-element-slot-id': 'contact-listening-streaming-started',
    }
  );

  useEffect(() => {
    if (blobUrl) {
      audioRef.current.playbackRate = Number(playSpeed);
    }
  }, [playSpeed]);

  const resetAudio = () => {
    setDuration(0);
    setSampledAudio(undefined);
    setCurrentTime(0);
    setPlayAudio(false);
    setCanvasState(CanvasState.PREPARING);
    setCanvasDrawn(false);
    setBlobUrl(undefined);
    setBuffers([]);
    setYetToLoadParts(true);
    setUpdatedBlobParams(nextBlobParams);
    setAudioBuffer(undefined);
    setNextContactPart(defaultContactData);
    setNextContactPartLoaded(DataState.INITIALIZED);
    isInitialLoad.current = true;
  };

  // Process the 1st part
  useEffect(() => {
    if (firstBase64 && nextBlobParams) {
      resetAudio();
      const bytes = Buffer.from(firstBase64, 'base64');
      const firstBlob = new Blob([bytes], {
        type:
          nextBlobParams.queryType?.queryTypeOption ==
            ContactListeningQueryTypeOption.CONNECT_PART ||
          nextBlobParams.queryType?.queryTypeOption ==
            ContactListeningQueryTypeOption.DART_CONNECT_PART
            ? 'audio/wav'
            : 'audio/mpeg',
      });
      const firstBlobUrl = URL.createObjectURL(firstBlob);
      setBlobUrl(firstBlobUrl);
      setBuffers([bytes]);
    }
  }, [id]);

  // Get next part(s) : getContactListeningData api call
  useEffect(() => {
    const apiNaws = DataSource.getDataFeed('NAWS');
    if (updatedBlobParams && updatedBlobParams.queryType?.part) {
      apiNaws.getContactListeningData(
        updatedBlobParams,
        setNextContactPart,
        setNextContactPartLoaded
      );
    }
    if (updatedBlobParams && !updatedBlobParams.queryType?.part) {
      logger.info(
        '[AudioPlayer] No more parts to load, setting yetToLoadParts=false'
      );
      setYetToLoadParts(false);
    }
  }, [updatedBlobParams]);

  // Process the part after receiving data from getContactListeningData api call
  useEffect(() => {
    if (updatedBlobParams && nextContactPartLoaded === DataState.LOADED) {
      let phoneRecord;
      if (
        updatedBlobParams.queryType?.queryTypeOption ==
        ContactListeningQueryTypeOption.CONNECT_PART
      ) {
        if (nextContactPart?.contact?.medium.connectPhoneRecordList?.length) {
          phoneRecord = nextContactPart.contact.medium.connectPhoneRecordList
            .filter(
              r => r.connectContactId === updatedBlobParams.queryType?.connectid
            )
            .shift();
        }
      } else if (
        updatedBlobParams.queryType?.queryTypeOption ==
        ContactListeningQueryTypeOption.DART_CONNECT_PART
      ) {
        if (nextContactPart?.contact?.dartEscalationContactList?.length) {
          if (
            nextContactPart.contact.dartEscalationContactList[0].medium
              .connectPhoneRecordList?.length
          ) {
            phoneRecord =
              nextContactPart.contact.dartEscalationContactList[0].medium.connectPhoneRecordList
                .filter(
                  r =>
                    r.connectContactId ===
                    updatedBlobParams.queryType?.connectid
                )
                .shift();
          }
        }
      }
      let newBytes;
      if (phoneRecord?.callRecordingPart?.base64) {
        newBytes = Buffer.from(
          phoneRecord?.callRecordingPart?.base64.toString(),
          'base64'
        );
      }
      if (newBytes && buffers) {
        const newBuffers = [...buffers];
        newBuffers.push(newBytes);
        setBuffers(newBuffers);
      }
      const newUpdatedBlobParams = { ...updatedBlobParams };
      newUpdatedBlobParams.queryType = {
        ...updatedBlobParams.queryType,
        part: phoneRecord?.callRecordingPart?.nextPart,
      };
      if (!newUpdatedBlobParams.queryType.part) {
        setYetToLoadParts(false);
      }
      setUpdatedBlobParams(newUpdatedBlobParams);
    }
  }, [nextContactPart, nextContactPartLoaded]);

  // All the parts are present in buffer array, now set audioBuffer and blobUrl
  useEffect(() => {
    async function finishLoader(): Promise<void> {
      if (nextBlobParams && !yetToLoadParts) {
        logger.info(
          `[AudioPlayer] finishLoader: combining ${buffers.length} chunks`
        );
        const audioContext = new AudioContext();
        const ab = await audioContext.decodeAudioData(
          // @ts-ignore
          Buffer.concat(buffers).buffer
        );
        setAudioBuffer(ab);

        const blob = new Blob([...buffers], {
          type:
            nextBlobParams.queryType?.queryTypeOption ==
              ContactListeningQueryTypeOption.CONNECT_PART ||
            nextBlobParams.queryType?.queryTypeOption ==
              ContactListeningQueryTypeOption.DART_CONNECT_PART
              ? 'audio/wav'
              : 'audio/mpeg',
        });
        const blobUrl = URL.createObjectURL(blob);
        setBlobUrl(blobUrl);
        setCanvasState(CanvasState.READY);
      }
    }
    if (!yetToLoadParts) {
      finishLoader();
    }
  }, [yetToLoadParts]);

  // Blob updated: on 1st chunk and after finishLoader (all chunks done).
  // Preserves playback position across src swaps.
  useEffect(() => {
    if (blobUrl && audioRef && audioRef.current) {
      isSwapping.current = true;
      const savedTime = audioRef.current.currentTime;
      const shouldAutoPlay = isInitialLoad.current || !audioRef.current.paused;
      audioRef.current.src = blobUrl;
      audioRef.current.oncanplay = () => {
        audioRef.current.currentTime = savedTime;
        // Setting audio.src resets playbackRate to 1 — restore user's selected speed
        audioRef.current.playbackRate = Number(playSpeed);
        if (shouldAutoPlay) {
          audioRef.current.play();
          setPlayAudio(true);
        }
        isSwapping.current = false;
        logger.info(
          `[AudioPlayer] Blob set, position=${savedTime.toFixed(1)}s (initial=${isInitialLoad.current})`
        );
        isInitialLoad.current = false;
        audioRef.current.oncanplay = null;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blobUrl]);

  // Final step : audioBuffer has all the parts, now show canvas.
  useEffect(() => {
    if (audioBuffer) {
      const processesSampledAudio: SampledAudio = processBuffer(audioBuffer);
      setSkipSilence(true);
      setSampledAudio(processesSampledAudio);
      setDuration(audioBuffer.duration);
    }
  }, [audioBuffer]);

  // render
  return firstBase64 ? (
    <Column heights='fill' width='100%'>
      <Row width='100%' widths='fill'>
        <Checkbox
          checked={skipSilence}
          onChange={val => {
            setSkipSilence(val);
            skipSilenceClick();
          }}
        >
          {intl.formatMessage({
            id: 'cs_perfportal_contact_listening_call_recording_skip_silence',
            defaultMessage: 'Skip Silence',
            description: 'Skip Silence',
          })}
        </Checkbox>
        <Row alignmentHorizontal='end'>
          <Text color={'secondary'} alignment='end'>
            {intl.formatMessage({
              id: 'cs_perfportal_contact_listening_call_recording_speed',
              defaultMessage: 'Speed',
              description: 'Speed',
            }) + ':'}
          </Text>
          <div style={{ zIndex: 99 }}>
            <Select
              value={playSpeed}
              onChange={val => {
                setPlaySpeed(val);
                changeSpeedClick();
              }}
              placeholder={intl.formatMessage({
                id: 'cs_perfportal_contact_listening_call_recording_speed',
                defaultMessage: 'Speed',
                description: 'Speed',
              })}
              size='small'
            >
              <SelectOption value='1' label='1x' key='1' />
              <SelectOption value='1.25' label='1.25x' key='1.25' />
              <SelectOption value='1.5' label='1.5x' key='1.5' />
              <SelectOption value='1.75' label='1.75x' key='1.75' />
              <SelectOption value='2' label='2x' key='2' />
            </Select>
          </div>
        </Row>
      </Row>
      <WaveCanvas
        id={id}
        sampledAudio={sampledAudio}
        currentTime={currentTime}
        canvasState={canvasState}
        setRatio={setRatio}
        setCanvasDrawn={setCanvasDrawn}
      />
      {!canvasDrawn && (
        <Row width='100%' widths='fill' alignmentVertical='center'>
          <Loader type='linear' size='small' />
        </Row>
      )}
      <Row width='100%' widths={['grid-1', 'grid-3']}>
        <Button
          type='icon'
          circular={true}
          disabled={!canvasDrawn}
          onClick={buttonClick}
        >
          {playAudio ? <Icon tokens={pause} /> : <Icon tokens={play} />}
        </Button>
        <Row>
          <Text type='b400' color={'primary'} alignment='end'>
            {locationAsStringCurrent()}
          </Text>
          <Text type='b400' color={'secondary'} alignment='start'>
            {locationAsStringDuration()}
          </Text>
        </Row>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio
          ref={audioRef}
          autoPlay={true}
          crossOrigin={'anonymous'}
          onLoadedData={(): void =>
            emitContactListeningStartedClickstreamEvent()
          }
          onTimeUpdate={(event): void => onTimeUpdate(event)}
          onEnded={onAudioEnded}
        />
      </Row>
      <PlayButtonTrackingTag />
      <SkipSilenceTrackingTag />
      <ChangeSpeedTrackingTag />
      <ContactListeningStartedClickstreamTag />
    </Column>
  ) : (
    <></>
  );
};
export default AudioPlayer;
